import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { formatPhone, isValidPhone, toApiPhone } from '../../lib/phone';

const API_BASE = process.env.REACT_APP_BACKEND_URL ?? '';
const STORAGE_KEY = 'kladoffka24:chat';

const WAREHOUSES = [
  { value: '18', label: 'Ячейка №1 — мкр Любимово, д. 18' },
  { value: '7',  label: 'Ячейка №2 — мкр Любимово, д. 7'  },
  { value: '11', label: 'Ячейка №3 — мкр Любимово, д. 11' },
];

// --- storage helpers ---
const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s.session_id) return null;
    return s;
  } catch {
    return null;
  }
};
const saveState = (s) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {}
};

const MaxChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [hintShown, setHintShown] = useState(false);

  // Persisted state
  const [sessionId, setSessionId] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [messages, setMessages] = useState([]); // [{role:'client'|'operator', text, ts, id?}]
  const lastIdRef = useRef(0);

  // Form (when no session) state
  const [draftName, setDraftName] = useState('');
  const [draftPhone, setDraftPhone] = useState('');
  const [draftWarehouse, setDraftWarehouse] = useState('');
  const [draftConsent, setDraftConsent] = useState(false);
  const [starting, setStarting] = useState(false);

  // Chat state
  const [draftMsg, setDraftMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [chatStatus, setChatStatus] = useState('active'); // active | closed | blocked
  const scrollRef = useRef(null);

  // Restore from localStorage
  useEffect(() => {
    const s = loadState();
    if (s) {
      setSessionId(s.session_id);
      setClientName(s.name || '');
      setClientPhone(s.phone || '');
      setMessages(s.messages || []);
      lastIdRef.current = s.last_id || 0;
    }
  }, []);

  // Persist on changes
  useEffect(() => {
    if (sessionId) {
      saveState({
        session_id: sessionId,
        name: clientName,
        phone: clientPhone,
        messages,
        last_id: lastIdRef.current,
      });
    }
  }, [sessionId, clientName, clientPhone, messages]);

  // Hint bubble — показываем через 3 сек, скрываем через 8 сек
  // (чтобы не перекрывал карточки бокса M в каталоге и не маячил вечно)
  useEffect(() => {
    const showT = setTimeout(() => setHintShown(true), 3000);
    const hideT = setTimeout(() => setHintShown(false), 11000);
    return () => { clearTimeout(showT); clearTimeout(hideT); };
  }, []);

  // На странице каталога подсказку вообще не показываем —
  // она перекрывает третью карточку (M) и создаёт впечатление, что карточка «съезжает».
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/catalog')) {
      setHintShown(false);
    }
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // --- Polling for operator replies ---
  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await axios.get(`${API_BASE}/api/chat/poll`, {
        params: { session_id: sessionId, after_id: lastIdRef.current },
        timeout: 8000,
      });
      if (data?.messages?.length) {
        setMessages((prev) => [
          ...prev,
          ...data.messages.map((m) => ({
            id: m.id,
            role: 'operator',
            text: m.text,
            ts: m.created_at,
          })),
        ]);
        lastIdRef.current = data.last_id;
      } else if (typeof data?.last_id === 'number' && data.last_id > lastIdRef.current) {
        lastIdRef.current = data.last_id;
      }
      if (data?.status && data.status !== chatStatus) {
        setChatStatus(data.status);
      }
    } catch (e) {
      // silent
    }
  }, [sessionId, chatStatus]);

  useEffect(() => {
    if (!sessionId) return;
    const interval = setInterval(poll, 4000);
    poll(); // immediate
    return () => clearInterval(interval);
  }, [sessionId, poll]);

  // --- Уведомление "клиент ушёл с сайта" через sendBeacon ---
  // pagehide надёжнее beforeunload (поддерживает back-forward cache, мобильные)
  useEffect(() => {
    if (!sessionId) return;
    const notifyLeave = () => {
      if (chatStatus !== 'active') return;
      try {
        const url = `${API_BASE}/api/chat/closed-form`;
        // form-urlencoded → не требует preflight, проходит даже когда страница уже unloading
        const params = new URLSearchParams();
        params.append('session_id', sessionId);
        params.append('reason', 'user_left');
        if (navigator.sendBeacon) {
          const blob = new Blob([params.toString()], { type: 'application/x-www-form-urlencoded' });
          navigator.sendBeacon(url, blob);
        } else {
          // Fallback — fetch keepalive
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: params.toString(),
            keepalive: true,
          }).catch(() => {});
        }
      } catch {}
    };
    window.addEventListener('pagehide', notifyLeave);
    window.addEventListener('beforeunload', notifyLeave);
    return () => {
      window.removeEventListener('pagehide', notifyLeave);
      window.removeEventListener('beforeunload', notifyLeave);
    };
  }, [sessionId, chatStatus]);

  // --- Start session ---
  const startChat = async () => {
    const name = draftName.trim();
    if (name.length < 2 || !draftWarehouse || !draftConsent) return;
    if (!isValidPhone(draftPhone)) return;
    setStarting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/api/chat/start`, {
        name,
        phone: toApiPhone(draftPhone),
        warehouse: draftWarehouse || undefined,
        consent: true,
      });
      if (data?.session_id) {
        setSessionId(data.session_id);
        setClientName(name);
        setClientPhone(phone);
        setMessages([
          {
            role: 'operator',
            text: `Здравствуйте, ${name}! Опишите ваш вопрос — оператор ответит здесь же в ближайшее время.`,
            ts: new Date().toISOString(),
            id: 0,
          },
        ]);
      }
    } catch (e) {
      // показать ошибку?
    } finally {
      setStarting(false);
    }
  };

  // --- Send message ---
  const sendMessage = async () => {
    const text = draftMsg.trim();
    if (!text || !sessionId || sending || chatStatus !== 'active') return;
    setSending(true);
    // оптимистично добавляем
    setMessages((prev) => [
      ...prev,
      { role: 'client', text, ts: new Date().toISOString() },
    ]);
    setDraftMsg('');
    try {
      await axios.post(`${API_BASE}/api/chat/send`, { session_id: sessionId, text });
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'operator',
          text: '⚠️ Не удалось отправить сообщение. Попробуйте ещё раз.',
          ts: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  // --- Reset (на случай если клиент хочет начать заново — пока без UI, можно добавить кнопку) ---
  // localStorage.removeItem(STORAGE_KEY); setSessionId(null); setMessages([]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleStartKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      startChat();
    }
  };

  return (
    <>
      {/* Popup */}
      {open && (
        <div className="fixed bottom-28 right-4 md:right-6 z-50 w-[340px] max-w-[calc(100vw-32px)] bg-white border-2 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] animate-in slide-in-from-bottom-4 flex flex-col max-h-[70vh]">
          {/* Header */}
          <div className="flex items-center justify-between bg-[#005FF9] px-4 py-3 shrink-0">
            <div className="flex items-center gap-2">
              <img src="/images/logo.png" alt="Кладоффка24" className="w-8 h-8 object-contain rounded-full bg-white p-0.5" />
              <div>
                <div className="text-white font-bold text-sm leading-tight">Кладоффка24</div>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-white/70 text-xs">оператор на связи</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
              aria-label="Закрыть"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* ====== STARTING FORM (нет сессии) ====== */}
          {!sessionId && (
            <div className="p-4 space-y-3 overflow-y-auto">
              <div className="flex items-start gap-2">
                <img src="/images/logo.png" alt="" className="w-8 h-8 rounded-full object-contain bg-gray-100 p-0.5 shrink-0 mt-0.5" />
                <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-gray-800 max-w-[250px]">
                  Здравствуйте! Чтобы начать диалог, оставьте имя и телефон 📦
                </div>
              </div>

              <div className="space-y-2">
                <input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={handleStartKeyDown}
                  placeholder="Ваше имя"
                  className="w-full border-2 border-black bg-gray-50 px-3 py-2 text-sm font-mono outline-none focus:border-[#005FF9]"
                />
                <input
                  value={draftPhone}
                  onChange={(e) => setDraftPhone(e.target.value)}
                  onKeyDown={handleStartKeyDown}
                  placeholder="+7 (999) 000-00-00"
                  type="tel"
                  className="w-full border-2 border-black bg-gray-50 px-3 py-2 text-sm font-mono outline-none focus:border-[#005FF9]"
                />
                <select
                  value={draftWarehouse}
                  onChange={(e) => setDraftWarehouse(e.target.value)}
                  className={`w-full border-2 bg-gray-50 px-3 py-2 text-sm font-mono outline-none focus:border-[#005FF9] cursor-pointer ${draftWarehouse ? 'border-black' : 'border-red-400'}`}
                >
                  <option value="">— Выберите ячейку * —</option>
                  {WAREHOUSES.map((w) => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draftConsent}
                    onChange={(e) => setDraftConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#005FF9] cursor-pointer flex-shrink-0"
                  />
                  <span className="font-mono text-[10px] text-gray-600 leading-tight">
                    Принимаю <a href="/oferta" target="_blank" rel="noopener noreferrer" className="underline text-[#005FF9] font-bold">оферту</a>, <a href="/rules" target="_blank" rel="noopener noreferrer" className="underline text-[#005FF9] font-bold">правила</a> и согласен на обработку персональных данных (<a href="/privacy" target="_blank" className="underline text-[#005FF9]">политика</a>)
                  </span>
                </label>

                <button
                  onClick={startChat}
                  disabled={starting || draftName.trim().length < 2 || !draftWarehouse || !draftConsent}
                  className="w-full flex items-center justify-center gap-2 bg-[#005FF9] hover:bg-[#0050d4] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-mono text-sm uppercase tracking-wider py-3 transition-colors"
                >
                  {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Начать чат
                </button>
              </div>

              <div className="border-t border-gray-200 pt-3 mt-3 text-center">
                <a
                  href="https://max.ru/id7751237158_2_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-mono text-[#005FF9] hover:underline"
                >
                  Или напишите нам в MAX →
                </a>
              </div>
            </div>
          )}

          {/* ====== CHAT (есть сессия) ====== */}
          {sessionId && (
            <>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFAFA]"
                style={{ minHeight: 280 }}
              >
                {messages.map((m, idx) => (
                  <div
                    key={m.id ?? `m-${idx}`}
                    className={`flex items-start gap-2 ${m.role === 'client' ? 'flex-row-reverse' : ''}`}
                  >
                    {m.role === 'operator' && (
                      <img src="/images/logo.png" alt="" className="w-7 h-7 rounded-full object-contain bg-gray-100 p-0.5 shrink-0 mt-0.5" />
                    )}
                    <div
                      className={`max-w-[230px] px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                        m.role === 'client'
                          ? 'bg-[#005FF9] text-white rounded-2xl rounded-tr-sm'
                          : 'bg-white border-2 border-black text-gray-800 rounded-2xl rounded-tl-sm'
                      }`}
                    >
                      {m.text}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    отправка...
                  </div>
                )}
              </div>

              {chatStatus !== 'active' ? (
                <div className="border-t-2 border-black bg-gray-100 px-4 py-4 text-center">
                  <div className="font-mono text-xs uppercase tracking-wider text-black/60 mb-1">
                    {chatStatus === 'blocked' ? '🚫 Диалог завершён' : '✅ Диалог завершён'}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Свяжитесь с нами: <a href="tel:+79184362255" className="underline font-semibold">+7 918 436-22-55</a>
                  </div>
                </div>
              ) : (
                <div className="flex items-end gap-2 border-t-2 border-black bg-gray-50 px-3 py-2 shrink-0">
                  <textarea
                    value={draftMsg}
                    onChange={(e) => setDraftMsg(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Напишите сообщение..."
                    rows={1}
                    className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 resize-none outline-none py-1 max-h-24"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!draftMsg.trim() || sending}
                    className="shrink-0 w-9 h-9 bg-[#005FF9] hover:bg-[#0050d4] disabled:bg-gray-300 flex items-center justify-center transition-colors"
                    aria-label="Отправить"
                  >
                    <Send className="w-4 h-4 text-white" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Floating button */}
      <div className="fixed bottom-6 right-4 md:right-6 z-50 flex flex-col items-end gap-2">
        {hintShown && !open && (
          <div className="relative flex items-center gap-2 bg-white border-2 border-black px-4 py-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] whitespace-nowrap">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse shrink-0" />
            <span className="font-bold text-sm text-black">Мы можем вам помочь?</span>
            <button onClick={() => setHintShown(false)} className="ml-1 text-gray-400 hover:text-black" aria-label="Скрыть подсказку">
              <X className="w-3 h-3" />
            </button>
            <div className="absolute -bottom-[10px] right-8 w-4 h-4 bg-white border-r-2 border-b-2 border-black rotate-45" />
          </div>
        )}

        <div className="relative">
          {!open && (
            <>
              <span className="pointer-events-none absolute inset-0 rounded-full bg-[#005FF9] opacity-30 animate-ping" />
              <span className="pointer-events-none absolute inset-[-8px] rounded-full border-2 border-[#005FF9] opacity-20 animate-ping" style={{ animationDelay: '0.4s' }} />
            </>
          )}
          <button
            onClick={() => { setOpen((v) => !v); setHintShown(false); }}
            className="relative w-20 h-20 rounded-full bg-white hover:bg-gray-50 border-[3px] border-black shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] flex items-center justify-center transition-all hover:scale-110 active:scale-95 active:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] overflow-hidden"
            aria-label="Написать нам"
          >
            {open
              ? <X className="w-7 h-7 text-black" />
              : <img src="/images/logo.png" alt="Кладоффка24" className="w-14 h-14 object-contain" />
            }
          </button>
        </div>
      </div>
    </>
  );
};

export default MaxChatWidget;
